import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../storage/local-storage.service';
import { AuthenticateService } from 'src/app/modules/authenticate/services/authenticate.service';
import { ProfileResolverService } from '../resolver/profile-resolver.service';
import { StateService } from '../storage/state.service';
import { Router } from '@angular/router';
import { SnackBarService } from '../snack-bar.service';
import { ExtraInfo } from '../../models/orders.model';

interface GatePayParams {
  merchantId: string;
  merchantTradeNo: string;
  prepayId: string;
  totalFee: string;
  currency: string;
}
interface MessageHandlers {
  gatepay: {
    postMessage: (params: GatePayParams) => void;
  };
}
// declare var gate; // Global GateIO Entry
declare global {
  interface Window {
    gateio: {
      gatepay: (
        merchantId: string,
        tradeNo: string,
        prepayId: string,
        totalFee: string,
        currency: string
      ) => void;
    };
    webkit?: {
      messageHandlers?: MessageHandlers;
    };
  }
}
@Injectable({
  providedIn: 'root',
})
export class GateIoService {
  tokenUrl = 'https://openplatform.gateapi.io/oauth/token';
  appClientId = environment.gateIoClientID;
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private localStorage: LocalStorageService,
    private authenticateService: AuthenticateService,
    private profileResolver: ProfileResolverService,
    private stateService: StateService,
    private zone: NgZone,
    private router: Router,
    private snackbarService: SnackBarService
  ) {}

  isGateIo() {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('gateio');
  }

  redirectToGateAuth() {
    // Don't redirect if OAuth flow already started or user is already logged in
    if (
      sessionStorage.getItem('gateio_oauth_started') === 'true' ||
      this.localStorage.getItem('accessToken')
    ) {
      return;
    }

    // Mark that we've started the OAuth flow
    sessionStorage.setItem('gateio_oauth_started', 'true');

    // Build and redirect to the authorization URL
    const authUrl =
      `https://openplatform.gateapi.io/oauth/authorize?` +
      `response_type=code&` +
      `client_id=${this.appClientId}&` +
      `redirect_uri=https://kingiftcard.com&` +
      `scope=read_profile,read_email,read_wallet&` +
      `state=654321`;
    window.location.href = authUrl;
  }

  gateIoLogin(code: string) {
    // Don't proceed if already logged in or no code provided
    if (this.localStorage.getItem('accessToken') || !code) {
      sessionStorage.removeItem('gateio_oauth_started');
      return;
    }

    this.authenticateService.gateIoLogin(code).subscribe(
      (res) => {
        if (res.token) {
          // Success - store tokens and update state
          this.localStorage.setItem('accessToken', res.token);
          this.localStorage.setItem('refreshToken', res.refresh_token);
          this.localStorage.setItem('hasGate', 'true');

          // Update application state
          this.stateService.setLoginStatus(true);
          this.profileResolver.updateInfo();

          // Navigate to home page within Angular zone to ensure change detection
          this.zone.run(() => {
            this.router.navigateByUrl('/');
          });
        } else {
        }

        // Always clean up the OAuth session state
        window.sessionStorage.removeItem('gateio_oauth_started');
      },
      (error) => {
        // Handle error case
        this.snackbarService.showSnackBar(
          error && error.error ? error.error.detail : 'Gate.io login failed',
          'primary',
          2000
        );

        // Clean up the OAuth session state even on error
        window.sessionStorage.removeItem('gateio_oauth_started');
      }
    );
  }

  isAndroid() {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('android');
  }

  isIOS() {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('ios');
  }

  handlePayment(extraInfo: ExtraInfo) {
    if (this.isAndroid()) {
      window.gateio.gatepay(
        extraInfo.MerchantId.toString(),
        extraInfo.merchantTradeNo,
        extraInfo.prepayId,
        extraInfo.totalFee,
        extraInfo.currency
      );
    } else if (this.isIOS()) {
      const params = {
        merchantId: extraInfo.MerchantId.toString(), // Replace with your merchant ID
        merchantTradeNo: extraInfo.merchantTradeNo,
        prepayId: extraInfo.prepayId,
        totalFee: extraInfo.totalFee,
        currency: extraInfo.currency,
      };

      window.webkit.messageHandlers.gatepay.postMessage(params);
    }
  }
}
